import type { InputManager } from "../core/InputManager";
import { stickAxis, touchLookDelta } from "../core/touchInput";
import { createButton, createElement } from "./dom";

export class MobileControls {
  public readonly element = createElement("section", "mobile-controls");
  public readonly orientationGuard = createElement("section", "orientation-guard hidden");
  private readonly media = window.matchMedia("(pointer: coarse)");
  private readonly events = new AbortController();
  private readonly stickBase = createElement("div", "mobile-stick-base");
  private readonly stickKnob = createElement("div", "mobile-stick-knob");
  private readonly useButton: HTMLButtonElement;
  private readonly weaponButton: HTMLButtonElement;
  private readonly sprintButton: HTMLButtonElement;
  private readonly mapButton: HTMLButtonElement;
  private readonly held = new Map<number, HTMLButtonElement>();
  private movePointer?: number;
  private lookPointer?: number;
  private moveX = 0;
  private moveY = 0;
  private lookX = 0;
  private lookY = 0;
  private visible = false;
  private touchMode = this.media.matches;
  private sprinting = false;
  private sensitivity = 1;

  public constructor(
    private readonly input: InputManager,
    private readonly root: HTMLElement,
    private readonly onEnvironmentChange: () => void,
  ) {
    const moveZone = createElement("div", "mobile-move-zone");
    moveZone.setAttribute("aria-label", "Движение: левый стик");
    this.stickBase.append(this.stickKnob);
    moveZone.append(this.stickBase);
    const lookZone = createElement("div", "mobile-look-zone");
    lookZone.setAttribute("aria-label", "Обзор: проведите пальцем");
    const fire = this.holdButton("ОГОНЬ", "fire", (held) => this.input.setVirtualFire(held));
    this.bindLook(lookZone);
    this.bindLook(fire);
    this.useButton = this.tapButton("ДЕЙСТВИЕ", "use", "KeyE");
    this.useButton.disabled = true;
    this.weaponButton = this.tapButton("ОРУЖИЕ", "weapon", "SwitchWeapon");
    this.mapButton = this.tapButton("КАРТА", "map", "Tab");
    this.sprintButton = createButton(
      "БЕГ",
      () => {
        if (!this.isActive) return;
        this.sprinting = !this.sprinting;
        this.sprintButton.setAttribute("aria-pressed", String(this.sprinting));
        this.input.setVirtualSprint(this.sprinting);
      },
      "mobile-button sprint",
    );
    this.sprintButton.setAttribute("aria-pressed", "false");
    this.sprintButton.title = "Включить или выключить бег";
    this.element.append(
      moveZone,
      lookZone,
      fire,
      this.useButton,
      this.tapButton("ЗАРЯДИТЬ", "reload", "KeyR"),
      this.weaponButton,
      this.mapButton,
      this.tapButton("Ⅱ", "pause", "Escape"),
      this.sprintButton,
    );
    this.element.querySelector(".pause")?.setAttribute("aria-label", "Пауза");
    this.orientationGuard.setAttribute("role", "status");
    this.orientationGuard.append(
      createElement("div", "orientation-icon", "↻"),
      createElement("strong", undefined, "ПОВЕРНИТЕ УСТРОЙСТВО"),
      createElement("p", undefined, "Для спуска нужен альбомный экран. Игра приостановлена."),
    );

    const options = { signal: this.events.signal };
    moveZone.addEventListener(
      "pointerdown",
      (event) => {
        if (!this.isActive || this.movePointer !== undefined) return;
        event.preventDefault();
        this.movePointer = event.pointerId;
        this.moveX = event.clientX;
        this.moveY = event.clientY;
        const bounds = moveZone.getBoundingClientRect();
        this.stickBase.style.left = `${event.clientX - bounds.left}px`;
        this.stickBase.style.top = `${event.clientY - bounds.top}px`;
        moveZone.setPointerCapture(event.pointerId);
      },
      options,
    );
    moveZone.addEventListener(
      "pointermove",
      (event) => {
        if (event.pointerId !== this.movePointer) return;
        const dx = event.clientX - this.moveX;
        const dy = event.clientY - this.moveY;
        const scale = Math.min(1, 42 / (Math.hypot(dx, dy) || 1));
        this.stickKnob.style.transform = `translate(${dx * scale}px, ${dy * scale}px)`;
        const axis = stickAxis(dx, dy);
        this.input.setVirtualMovement(axis.x, axis.z);
      },
      options,
    );
    for (const type of ["pointerup", "pointercancel", "lostpointercapture"] as const) {
      moveZone.addEventListener(
        type,
        (event) => {
          if (event.pointerId !== this.movePointer) return;
          this.movePointer = undefined;
          this.stickKnob.style.transform = "";
          this.input.setVirtualMovement(0, 0);
        },
        options,
      );
    }
    this.media.addEventListener("change", () => this.setTouchMode(this.media.matches), options);
    root.addEventListener(
      "pointerdown",
      (event) => {
        if (event.pointerType === "touch") this.setTouchMode(true);
        else if (event.pointerType === "mouse") this.setTouchMode(false);
      },
      { ...options, capture: true },
    );
    window.addEventListener("resize", () => this.refreshEnvironment(), options);
    window.addEventListener("blur", () => this.reset(), options);
    this.sync();
  }

  public get enabled(): boolean {
    return this.touchMode;
  }
  public get portrait(): boolean {
    return this.enabled && window.innerHeight > window.innerWidth;
  }
  public get isActive(): boolean {
    return this.enabled && this.visible && !this.portrait;
  }

  public setSensitivity(value: number): void {
    this.sensitivity = value;
  }

  public setVisible(visible: boolean): void {
    this.visible = visible;
    if (!visible) this.reset();
    this.sync();
  }

  public setMapOpen(open: boolean): void {
    this.reset();
    this.element.classList.toggle("map-open", open);
    this.mapButton.textContent = open ? "ЗАКРЫТЬ" : "КАРТА";
    this.mapButton.setAttribute("aria-pressed", String(open));
  }

  public setContext(hasSecondaryWeapon: boolean, prompt?: string): void {
    this.weaponButton.disabled = !hasSecondaryWeapon;
    this.weaponButton.title = hasSecondaryWeapon
      ? "Сменить оружие"
      : "Сначала найдите другое оружие";
    this.useButton.disabled = !prompt;
    this.useButton.textContent = prompt?.includes("ЭВАК") ? "ВЫХОД" : "ДЕЙСТВИЕ";
    this.useButton.setAttribute("aria-label", prompt ?? "Нет объекта для взаимодействия");
  }

  public reset(): void {
    this.movePointer = undefined;
    this.lookPointer = undefined;
    for (const [id, button] of this.held) {
      if (button.hasPointerCapture(id)) button.releasePointerCapture(id);
      button.classList.remove("held");
    }
    this.held.clear();
    this.stickKnob.style.transform = "";
    this.stickBase.style.left = "";
    this.stickBase.style.top = "";
    this.sprinting = false;
    this.sprintButton.setAttribute("aria-pressed", "false");
    this.input.clear();
  }

  public dispose(): void {
    this.reset();
    this.events.abort();
  }

  private setTouchMode(touch: boolean): void {
    if (this.touchMode === touch) return;
    this.touchMode = touch;
    this.refreshEnvironment();
  }

  private refreshEnvironment(): void {
    this.reset();
    this.sync();
    this.onEnvironmentChange();
  }

  private sync(): void {
    this.root.dataset["input"] = this.enabled ? "touch" : "mouse";
    this.element.classList.toggle("active", this.isActive);
    this.orientationGuard.classList.toggle("hidden", !this.portrait);
  }

  private bindLook(zone: HTMLElement): void {
    const options = { signal: this.events.signal };
    zone.addEventListener(
      "pointerdown",
      (event) => {
        if (!this.isActive || this.lookPointer !== undefined) return;
        event.preventDefault();
        this.lookPointer = event.pointerId;
        this.lookX = event.clientX;
        this.lookY = event.clientY;
        zone.setPointerCapture(event.pointerId);
      },
      options,
    );
    zone.addEventListener(
      "pointermove",
      (event) => {
        if (event.pointerId !== this.lookPointer) return;
        const delta = touchLookDelta(
          event.clientX - this.lookX,
          event.clientY - this.lookY,
          this.sensitivity,
        );
        this.input.addVirtualLook(delta.x, delta.y);
        this.lookX = event.clientX;
        this.lookY = event.clientY;
      },
      options,
    );
    for (const type of ["pointerup", "pointercancel", "lostpointercapture"] as const) {
      zone.addEventListener(
        type,
        (event) => {
          if (event.pointerId === this.lookPointer) this.lookPointer = undefined;
        },
        options,
      );
    }
  }

  private tapButton(label: string, className: string, code: string): HTMLButtonElement {
    return createButton(
      label,
      () => {
        if (this.isActive) this.input.pressVirtualKey(code);
      },
      `mobile-button ${className}`,
    );
  }

  private holdButton(
    label: string,
    className: string,
    setHeld: (held: boolean) => void,
  ): HTMLButtonElement {
    const button = createButton(label, () => undefined, `mobile-button ${className}`);
    const options = { signal: this.events.signal };
    button.addEventListener(
      "pointerdown",
      (event) => {
        if (!this.isActive || this.held.size > 0) return;
        event.preventDefault();
        this.held.set(event.pointerId, button);
        button.classList.add("held");
        setHeld(true);
        button.setPointerCapture(event.pointerId);
      },
      options,
    );
    for (const type of ["pointerup", "pointercancel", "lostpointercapture"] as const) {
      button.addEventListener(
        type,
        (event) => {
          if (!this.held.delete(event.pointerId)) return;
          button.classList.remove("held");
          setHeld(false);
        },
        options,
      );
    }
    return button;
  }
}
